import React from 'react';
import { toast } from 'react-hot-toast';
import { FiMessageSquare } from 'react-icons/fi';

import { useAuth } from '../../contexts/AuthContext';

// Hilfsfunktion, um **Wörter** mit farbigem Highlighting umzuwandeln und Umlaute zu reparieren
function renderWithBold(text) {
  // Repariere Umlaute zuerst
  const cleanText = text
    .replace(/Ã¤/g, 'ä')
    .replace(/Ã¶/g, 'ö')
    .replace(/Ã¼/g, 'ü')
    .replace(/ÃŸ/g, 'ß')
    .replace(/Ã„/g, 'Ä')
    .replace(/Ã–/g, 'Ö')
    .replace(/Ãœ/g, 'Ü');

  // Ersetzt **Wort** durch <mark> für farbiges Highlighting
  const html = cleanText.replace(
    /\*\*(.+?)\*\*/g,
    '<mark class="bg-yellow-200 dark:bg-yellow-600 font-semibold px-1 rounded">$1</mark>',
  );
  return { __html: html };
}

// Hilfsfunktion für Dateinamen
function cleanFilename(filename) {
  return filename
    .replace(/Ã¤/g, 'ä')
    .replace(/Ã¶/g, 'ö')
    .replace(/Ã¼/g, 'ü')
    .replace(/ÃŸ/g, 'ß')
    .replace(/Ã„/g, 'Ä')
    .replace(/Ã–/g, 'Ö')
    .replace(/Ãœ/g, 'Ü');
}

function SnippetDisplay({ snippets, onViewSnippet }) {
  const { isDarkMode } = useAuth();

  const handleSnippetClick = async (snippet) => {
    if (!snippet.document_id) return;

    // Bereinige Text von Markdown und nimm die ersten 10 Wörter
    const cleanText = snippet.text
      .replace(/\*\*(.+?)\*\*/g, '$1') // Entferne **fett**
      .replace(/\*(.+?)\*/g, '$1') // Entferne *kursiv*
      .trim();

    const words = cleanText.split(/\s+/);
    const searchText = words.slice(0, Math.min(10, words.length)).join(' ');

    // Kopiere in Zwischenablage
    try {
      await navigator.clipboard.writeText(searchText);
      toast.success('📋 Suchtext kopiert! Nutze Strg+F im Dokument', { duration: 3000 });
    } catch (err) {
      console.error('Clipboard-Fehler:', err);
      toast.info('💡 Nutze Strg+F im Dokument zum Suchen', { duration: 3000 });
    }

    // Öffne Dokument (ohne searchText, da automatische Suche nicht funktioniert)
    onViewSnippet(snippet.document_id, snippet.filename, null);
  };

  return (
    <div
      className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg p-3 h-full flex flex-col`}
    >
      <div className="flex-shrink-0 mb-2">
        <h3
          className={`text-sm font-semibold flex items-center gap-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}
        >
          <FiMessageSquare className="text-blue-400" />
          Relevante Ausschnitte
          {snippets.length > 0 && (
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              ({snippets.length})
            </span>
          )}
        </h3>
        {snippets.length > 0 && (
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
            💡 Tipp: Klicken kopiert Text → Im Dokument mit Strg+F suchen
          </p>
        )}
      </div>
      {snippets.length > 0 ? (
        <div className="overflow-y-auto flex-grow">
          <ul className="space-y-2">
            {snippets.map((snippet, index) => (
              <li key={index}>
                <div
                  onClick={() => handleSnippetClick(snippet)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && snippet.document_id) {
                      e.preventDefault();
                      handleSnippetClick(snippet);
                    }
                  }}
                  role="button"
                  tabIndex={snippet.document_id ? 0 : -1}
                  className={`p-2 rounded-md border-l-4 border-blue-500 transition-all ${
                    snippet.document_id
                      ? `cursor-pointer ${isDarkMode ? 'bg-gray-700/50 hover:bg-blue-900/50 hover:border-blue-400' : 'bg-blue-50 hover:bg-blue-600 hover:text-white'}`
                      : isDarkMode
                        ? 'bg-gray-700/50'
                        : 'bg-blue-50'
                  }`}
                  title={snippet.document_id ? 'Klicken kopiert Suchtext und öffnet Dokument' : ''}
                >
                  <p
                    className={`text-xs mb-2 leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}
                    dangerouslySetInnerHTML={renderWithBold(snippet.text)}
                  />
                  <div
                    className={`flex justify-between items-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    <span className="truncate flex-1 mr-2" title={cleanFilename(snippet.filename)}>
                      📄 {cleanFilename(snippet.filename)}
                    </span>
                    <span className="font-mono text-blue-400 text-xs flex-shrink-0">
                      {snippet.score.toFixed(2)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div
          className={`flex-grow flex flex-col justify-center items-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
        >
          <FiMessageSquare className="text-2xl mb-2 opacity-50" />
          <p className="text-xs text-center px-4">
            Hier werden die Textausschnitte angezeigt, die zur Antwort geführt haben.
          </p>
        </div>
      )}
    </div>
  );
}

export default SnippetDisplay;
